import { useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import axios from "axios";
import httpServices, { getApiBaseUrl } from "../../../services/httpServices";
import "./courseImportWizard.css";

/**
 * Build a course from uploaded PDFs.
 *
 * Upload -> review -> publish. The first two steps save nothing: the draft lives
 * in this component's state, so the author can revise as often as they like and
 * abandon the whole thing by navigating away. Publishing is the only write, and
 * it is always a deliberate click.
 */

const SEV = {
  critical: { cls: "ciw-crit", icon: "⛔", label: "Needs your confirmation" },
  high: { cls: "ciw-high", icon: "⚠️", label: "Significant" },
  medium: { cls: "ciw-med", icon: "•", label: "Moderate" },
  low: { cls: "ciw-low", icon: "•", label: "Minor" },
  error: { cls: "ciw-crit", icon: "⛔", label: "Needs fixing" },
  warning: { cls: "ciw-high", icon: "⚠️", label: "Worth a look" },
  info: { cls: "ciw-low", icon: "ℹ️", label: "For your information" },
};

function FindingList({ items, emptyText }) {
  if (!items || items.length === 0) {
    return <p className="ciw-muted">{emptyText}</p>;
  }
  const groups = Object.keys(SEV)
    .map((s) => [s, items.filter((i) => i.severity === s)])
    .filter(([, v]) => v.length > 0);

  return (
    <div className="ciw-findings">
      {groups.map(([sev, list]) => (
        <div key={sev} className={`ciw-fgroup ${SEV[sev].cls}`}>
          <div className="ciw-fhead">
            <span>{SEV[sev].icon}</span>
            <strong>{SEV[sev].label}</strong>
            <span className="ciw-badge">{list.length}</span>
          </div>
          <ul>
            {list.map((f, i) => (
              <li key={i}>
                {f.moduleNumber ? <b>M{f.moduleNumber} </b> : null}
                {f.message || f.detail}
                {f.from && f.to ? (
                  <div className="ciw-fromto">
                    <div><span>from</span> {f.from}</div>
                    <div><span>to</span> {f.to}</div>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
FindingList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  emptyText: PropTypes.string,
};
FindingList.defaultProps = { items: [], emptyText: "Nothing to report." };

const CourseImportWizard = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState("upload");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const [coursePdf, setCoursePdf] = useState(null);
  const [answerGuide, setAnswerGuide] = useState(null);

  const [draft, setDraft] = useState(null);
  const [document_, setDocument_] = useState(null);
  const [validation, setValidation] = useState(null);
  const [needsReview, setNeedsReview] = useState(null);

  const [instruction, setInstruction] = useState("");
  const [allowAnswerChanges, setAllowAnswerChanges] = useState(false);
  const [diff, setDiff] = useState(null);
  const [modelNotes, setModelNotes] = useState("");

  const [meta, setMeta] = useState({
    title: "", certificateTitle: "", description: "", category: "",
    expirationMonths: 12, hours: "", passingScore: 80, isActive: false, isRequired: false,
  });
  const [published, setPublished] = useState(null);

  const setMetaField = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setMeta((m) => ({ ...m, [k]: v }));
  };

  async function handleExtract(e) {
    e.preventDefault();
    if (!coursePdf) { setError("Choose the course PDF first."); return; }
    setError(""); setBusy("Reading the PDF and building the draft. This takes a few minutes for a full course.");
    try {
      const fd = new FormData();
      fd.append("coursePdf", coursePdf);
      if (answerGuide) fd.append("answerGuide", answerGuide);
      fd.append("passingScore", String(meta.passingScore));

      // axios directly, not httpServices: its post() does `{ ...body, ipaddress }`,
      // and spreading a FormData yields an empty object, so the files never leave
      // the browser and the server sees no coursePdf. CourseBuilder uploads its
      // PDF the same way, for the same reason.
      const token = localStorage.getItem("token");
      const base = getApiBaseUrl().replace(/\/+$/, "");
      const { data: res } = await axios.post(
        `${base}/admin/custom-training/courses/extract-draft`,
        fd,
        {
          headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` },
          // A full course is roughly ten modules of sequential model calls.
          timeout: 15 * 60 * 1000,
        }
      );

      if (!res.success) { setError(res.error || "Could not read this document."); return; }

      setDraft({ modules: res.draft.modules, questions: res.draft.questions });
      setDocument_(res.document);
      setValidation(res.validation);
      setNeedsReview(res.needsReview);
      setMeta((m) => ({
        ...m,
        title: m.title || coursePdf.name.replace(/\.pdf$/i, "").replace(/[-_]+/g, " "),
      }));
      setStep("review");
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Extraction failed.");
    } finally { setBusy(""); }
  }

  async function handleRevise() {
    if (!instruction.trim()) return;
    setError(""); setBusy("Applying your change…");
    try {
      const token = localStorage.getItem("token");
      const base = getApiBaseUrl().replace(/\/+$/, "");
      const { data: res } = await axios.post(
        `${base}/admin/custom-training/courses/revise-draft`,
        { draft, instruction, allowAnswerChanges },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 10 * 60 * 1000 }
      );
      if (!res.success) { setError(res.error || "Revision failed."); return; }
      setDraft(res.draft);
      setDiff(res.diff);
      setModelNotes(res.modelNotes || "");
      setValidation(res.validation);
      setInstruction("");
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Revision failed.");
    } finally { setBusy(""); }
  }

  async function handlePublish() {
    setError(""); setBusy("Publishing…");
    try {
      // Store the source PDF before publishing. extract-draft reads it in memory
      // and keeps nothing, so without this the course is created with a filename
      // but no pdfUrl - and the learner gets a "View PDF" link that 404s.
      let pdfInfo = null;
      if (coursePdf) {
        setBusy("Storing the course document…");
        const token = localStorage.getItem("token");
        const base = getApiBaseUrl().replace(/\/+$/, "");
        const fd = new FormData();
        fd.append("pdf", coursePdf);
        const up = await axios.post(`${base}/admin/custom-training/upload-temp-pdf`, fd, {
          headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` },
          timeout: 120000,
        });
        if (up.data && up.data.success) pdfInfo = up.data.file;
        setBusy("Publishing…");
      }

      const res = await httpServices.post("/admin/custom-training/courses/publish-draft", {
        draft,
        passingScore: parseInt(meta.passingScore, 10) || 80,
        course: {
          title: meta.title,
          certificateTitle: meta.certificateTitle || meta.title,
          description: meta.description || null,
          category: meta.category || null,
          expirationMonths: parseInt(meta.expirationMonths, 10) || 12,
          hours: meta.hours ? parseFloat(meta.hours) : null,
          isActive: meta.isActive,
          isRequired: meta.isRequired,
          pdfFileName: pdfInfo ? pdfInfo.originalName : (coursePdf ? coursePdf.name : "course.pdf"),
          pdfS3Key: pdfInfo ? pdfInfo.filename : null,
          pdfUrl: pdfInfo ? pdfInfo.url : null,
        },
      });
      if (!res.success) {
        setError(res.error || "Publish failed.");
        setValidation((v) => ({ ...(v || {}), findings: res.structuralErrors || (v && v.findings) || [] }));
        setNeedsReview((n) => ({ ...(n || {}), unresolvedAnswers: (res.unresolvedAnswers || []).length }));
        return;
      }
      setPublished(res);
      setStep("done");
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Publish failed.");
    } finally { setBusy(""); }
  }

  const unresolved = needsReview ? needsReview.unresolvedAnswers : 0;
  const canPublish = draft && unresolved === 0
    && !(validation && validation.counts && validation.counts.error > 0)
    && meta.title.trim();

  return (
    <div className="ciw">
      <div className="ciw-top">
        <h2>Build a course from a PDF</h2>
        <button type="button" className="ciw-btn ghost" onClick={() => navigate("/admin/custom-training")}>
          Back to courses
        </button>
      </div>

      <ol className="ciw-steps">
        {["upload", "review", "done"].map((s, i) => (
          <li key={s} className={step === s ? "on" : ""}>
            <span>{i + 1}</span>
            {s === "upload" ? "Upload" : s === "review" ? "Review and revise" : "Published"}
          </li>
        ))}
      </ol>

      {error && <div className="ciw-alert err">{error}</div>}
      {busy && <div className="ciw-alert busy">{busy}</div>}

      {/* ---------------- step 1 ---------------- */}
      {step === "upload" && (
        <form className="ciw-card" onSubmit={handleExtract}>
          <p className="ciw-lead">
            Upload the course document. Add the answer guide too if the course has one —
            without it the answers cannot be read from the document and you will be asked
            to set each one yourself.
          </p>

          <label className="ciw-file">
            <span>Course PDF <b>(required)</b></span>
            <input type="file" accept=".pdf" onChange={(e) => setCoursePdf(e.target.files[0])} />
            {coursePdf && <em>{coursePdf.name}</em>}
          </label>

          <label className="ciw-file">
            <span>Answer guide PDF (recommended)</span>
            <input type="file" accept=".pdf" onChange={(e) => setAnswerGuide(e.target.files[0])} />
            {answerGuide && <em>{answerGuide.name}</em>}
          </label>

          <div className="ciw-actions">
            <button type="submit" className="ciw-btn" disabled={!!busy || !coursePdf}>
              Build draft →
            </button>
          </div>
          <p className="ciw-muted">Nothing is saved at this step.</p>
        </form>
      )}

      {/* ---------------- step 2 ---------------- */}
      {step === "review" && draft && (
        <>
          <div className="ciw-card">
            <h3>What was found</h3>
            <div className="ciw-stats">
              <div><b>{document_?.pages ?? "—"}</b><span>pages</span></div>
              <div><b>{draft.modules.length}</b><span>modules</span></div>
              <div><b>{draft.questions.length}</b><span>questions</span></div>
              <div><b>{document_?.answerGuideProvided ? "Yes" : "No"}</b><span>answer guide</span></div>
              <div className={unresolved ? "warn" : ""}>
                <b>{unresolved}</b><span>answers to set</span>
              </div>
            </div>

            {unresolved > 0 && (
              <div className="ciw-alert warn">
                {unresolved} question(s) have no correct answer, because the document does not
                state one. Upload the answer guide and rebuild, or set them before publishing —
                a course cannot be published until every question can be graded.
              </div>
            )}

            <div className="ciw-modules">
              {draft.modules.map((m) => (
                <div key={m.moduleNumber} className="ciw-mod">
                  <b>Module {m.moduleNumber}</b>
                  <span>{m.title}</span>
                  <em>{(m.sections || []).length} sections · {m.questionCount ?? draft.questions.filter((q) => q.moduleNumber === m.moduleNumber).length} questions</em>
                </div>
              ))}
            </div>
          </div>

          <div className="ciw-card">
            <h3>Ask for a change</h3>
            <p className="ciw-muted">
              Describe what you want in your own words. Correct answers are protected
              unless you tick the box below, and every change is listed for you to check.
            </p>
            <textarea
              rows={3} value={instruction} placeholder='e.g. "Split module 4 into two sections" or "Shorten every explanation to one sentence"'
              onChange={(e) => setInstruction(e.target.value)}
            />
            <label className="ciw-check">
              <input type="checkbox" checked={allowAnswerChanges}
                onChange={(e) => setAllowAnswerChanges(e.target.checked)} />
              Allow this change to alter correct answers
            </label>
            <div className="ciw-actions">
              <button type="button" className="ciw-btn" disabled={!!busy || !instruction.trim()} onClick={handleRevise}>
                Apply change
              </button>
            </div>

            {modelNotes && <p className="ciw-notes">{modelNotes}</p>}
            {diff && (
              <>
                <h4>What actually changed</h4>
                <p className="ciw-muted">
                  Compared from the documents themselves, not from the description above.
                  Questions: {diff.questionsBefore} → {diff.questionsAfter}.
                </p>
                <FindingList items={diff.changes} emptyText="No differences were found." />
              </>
            )}
          </div>

          <div className="ciw-card">
            <h3>Notes on this course</h3>
            <p className="ciw-muted">
              Advisory only. None of this prevents publishing — it is here so you can decide
              what, if anything, to change.
            </p>
            <FindingList items={validation ? validation.findings : []} emptyText="Nothing to report." />
          </div>

          <div className="ciw-card">
            <h3>Publish</h3>
            <div className="ciw-grid">
              <label>Course title *<input value={meta.title} onChange={setMetaField("title")} /></label>
              <label>Certificate title<input value={meta.certificateTitle} onChange={setMetaField("certificateTitle")} placeholder="defaults to the course title" /></label>
              <label>Category<input value={meta.category} onChange={setMetaField("category")} /></label>
              <label>Valid for (months)<input type="number" min="1" value={meta.expirationMonths} onChange={setMetaField("expirationMonths")} /></label>
              <label>Training hours<input type="number" step="0.25" value={meta.hours} onChange={setMetaField("hours")} /></label>
              <label>Passing score (%)<input type="number" min="1" max="100" value={meta.passingScore} onChange={setMetaField("passingScore")} /></label>
            </div>
            <label className="ciw-desc">Description<textarea rows={2} value={meta.description} onChange={setMetaField("description")} /></label>

            <label className="ciw-check">
              <input type="checkbox" checked={meta.isRequired} onChange={setMetaField("isRequired")} />
              Required — assign to every teacher at these schools
            </label>
            <label className="ciw-check">
              <input type="checkbox" checked={meta.isActive} onChange={setMetaField("isActive")} />
              Active — make the course visible
            </label>
            <p className="ciw-muted">
              <b>Active</b> makes the course visible. <b>Required</b> is what actually puts it on
              teachers&apos; dashboards — a course that is Active but not Required is allowed but
              assigned to nobody, so nothing appears. Both are needed for learners to see it.
              You can change either later from the course list.
            </p>

            <div className="ciw-actions">
              <button type="button" className="ciw-btn primary" disabled={!!busy || !canPublish} onClick={handlePublish}>
                Publish course
              </button>
            </div>
            {!canPublish && (
              <p className="ciw-muted">
                {unresolved > 0
                  ? "Every question needs a correct answer before this can be published."
                  : !meta.title.trim() ? "A course title is required."
                    : "There are errors that would stop the quiz grading correctly."}
              </p>
            )}
          </div>
        </>
      )}

      {/* ---------------- step 3 ---------------- */}
      {step === "done" && published && (
        <div className="ciw-card ciw-done">
          <div className="ciw-tick">✓</div>
          <h3>{published.course.title} published</h3>
          <div className="ciw-stats">
            <div><b>{published.published.modules}</b><span>modules</span></div>
            <div><b>{published.published.questions}</b><span>questions</span></div>
            <div><b>{published.published.sections}</b><span>sections</span></div>
            <div><b>{published.published.isActive ? "Visible" : "Hidden"}</b><span>status</span></div>
            <div><b>{published.published.audience}</b><span>audience</span></div>
          </div>
          {published.assignment && published.assignment.assigned > 0 ? (
            <p className="ciw-muted">
              Assigned to <b>{published.assignment.assigned}</b> teacher
              {published.assignment.assigned === 1 ? "" : "s"}
              {published.assignment.alreadyHad
                ? ` (${published.assignment.alreadyHad} already had it)` : ""}.
              It is on their dashboards now.
            </p>
          ) : (
            <p className="ciw-muted">
              <b>Assigned to nobody yet.</b>{" "}
              {!published.published.isActive
                ? "The course is not Active, so it is hidden from learners."
                : "The course is Active but not Required, so it is visible but on no dashboards."}{" "}
              Tick <b>Required</b> on the course in the course list to assign it to every teacher
              at the selected schools.
            </p>
          )}
          <div className="ciw-actions">
            <button type="button" className="ciw-btn" onClick={() => navigate("/admin/custom-training")}>
              Go to courses
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseImportWizard;
